import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    ListItem,
    ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import throttle from 'lodash/throttle';
import { IOrderReadResourceV18 } from 'components/order/model/Order';
import { RecentOrder } from 'components/recentOrders/RecentOrder';
import { RecentOrderItemsSelection } from 'components/recentOrders/RecentOrderItemsSelection';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';

const PREFIX = 'RecentOrdersList';

const classes = {
    panelTitle: `${PREFIX}panelTitle`,
    panel: `${PREFIX}panel`,
    expansionPanelSummary: `${PREFIX}expansionPanelSummary`,
    expansionPanelIcon: `${PREFIX}expansionPanelIcon`,
    expansionPanelDetails: `${PREFIX}expansionPanelDetails`,
    iconColor: `${PREFIX}iconColor`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(0),
    flexDirection: 'column',

    [`& .${classes.panelTitle}`]: {
        fontWeight: 'bold',
        color: theme.palette.titleTextColour,
        marginLeft: theme.spacing(1.5)
    },

    [`& .${classes.panel}`]: {
        width: '100%',
        '&::before': {
            height: 0
        },
        marginTop: '0'
    },

    [`& .${classes.expansionPanelSummary}`]: {
        alignItems: 'flex-start',
        padding: theme.spacing(0, 1.5, 0, 0),
        borderBottom: `1px solid ${theme.palette.divider}`
    },

    [`& .${classes.expansionPanelIcon}`]: {
        color: theme.palette.text.primary,
        margin: theme.spacing(0, -0.5, 0, 0),
        '& svg': {
            fontSize: '32px'
        },
        alignSelf: 'center'
    },

    [`& .${classes.expansionPanelDetails}`]: {
        flexDirection: 'column',
        paddingLeft: 0,
        paddingRight: 0
    },

    [`& .${classes.iconColor}`]: {
        color: theme.palette.titleTextColour
    }
}));

interface IRecentOrdersListProps {
    orders: IOrderReadResourceV18[];
    isCollapsible?: boolean;
    setActiveCategory?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const RecentOrdersList: React.FC<React.PropsWithChildren<IRecentOrdersListProps>> = ({
    orders,
    isCollapsible = true,
    setActiveCategory
}) => {
    const { t } = useTranslation();
    const [scenarioFromRoute] = useRouteScenario();
    const [selectedRecentOrder, setSelectedRecentOrder] = React.useState<IOrderReadResourceV18 | null>();
    const { checkServiceAvailability, isLocationOrderEnabled, isWorkingHour } = useLocationHelpers();
    const isOrderingEnabled = React.useMemo(
        () =>
            !!scenarioFromRoute &&
            checkServiceAvailability(scenarioFromRoute) &&
            isLocationOrderEnabled &&
            isWorkingHour,
        [checkServiceAvailability, isLocationOrderEnabled, scenarioFromRoute, isWorkingHour]
    );

    const handleRecentOrderClick = React.useCallback(
        (order: IOrderReadResourceV18) => {
            setSelectedRecentOrder(order);
        },
        [setSelectedRecentOrder]
    );

    const renderOrderItem = React.useCallback(
        (order: IOrderReadResourceV18) => (
            <RecentOrder
                key={order.id}
                order={order}
                onOrderClick={handleRecentOrderClick}
                orderingEnabled={isOrderingEnabled}
            />
        ),
        [handleRecentOrderClick, isOrderingEnabled]
    );

    const handleRecentOrderSelectionClose = React.useCallback(() => {
        setSelectedRecentOrder(null);
    }, []);

    const handleActiveCategory = React.useMemo(
        () =>
            throttle((inView: boolean) => {
                if (inView && setActiveCategory) {
                    setActiveCategory('recent-orders');
                }
            }, 800),
        [setActiveCategory]
    );

    if (!!orders.length) {
        return (
            <StyledListItem>
                {isCollapsible ? (
                    <Accordion
                        elevation={0}
                        className={classes.panel}
                        slotProps={{
                            transition: { unmountOnExit: true }
                        }}
                    >
                        <AccordionSummary
                            className={classes.expansionPanelSummary}
                            classes={{
                                expandIconWrapper: classes.expansionPanelIcon
                            }}
                            expandIcon={<ExpandMoreIcon className={classes.iconColor} />}
                        >
                            <Box width="100%">
                                <ListItemText
                                    primary={t('PREORDER_PREVIOUS_ORDERS')}
                                    slotProps={{
                                        primary: { className: classes.panelTitle, variant: 'h5' }
                                    }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails classes={{ root: classes.expansionPanelDetails }}>
                            {orders.map(renderOrderItem)}
                        </AccordionDetails>
                    </Accordion>
                ) : (
                    <InView
                        rootMargin={'-110px 0px -80% 0px'}
                        onChange={handleActiveCategory}
                        style={{ width: '100%' }}
                        key="recent-orders"
                    >
                        <Box width="100%" marginTop={2} marginBottom={1}>
                            <ListItemText
                                primary={t('PREORDER_PREVIOUS_ORDERS')}
                                slotProps={{
                                    primary: { className: classes.panelTitle, variant: 'h5' }
                                }}
                            />
                        </Box>
                        <Divider />
                        <div> {orders.map(renderOrderItem)}</div>
                    </InView>
                )}
                {!!selectedRecentOrder && (
                    <RecentOrderItemsSelection
                        open={!!selectedRecentOrder}
                        onClose={handleRecentOrderSelectionClose}
                        order={selectedRecentOrder}
                        orderingEnabled={isOrderingEnabled}
                    />
                )}
            </StyledListItem>
        );
    }

    return null;
};
